// Small shared UI primitives + a tiny data-fetch hook used across W2 pages.
import { useCallback, useEffect, useRef, useState } from 'react';
import { hashTint, initial } from '../lib/format';
import { isLightColor } from '../lib/color';

export function useAsync(fn, deps = []) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoFn = useCallback(fn, deps);
  // Re-fetch in the background: no spinner, keeps current data if it fails.
  const fnRef = useRef(memoFn);
  fnRef.current = memoFn;
  const refresh = useCallback(() => Promise.resolve(fnRef.current())
    .then((data) => setState({ loading: false, error: null, data }))
    .catch(() => {}), []);
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    Promise.resolve(memoFn())
      .then((data) => { if (!cancelled) setState({ loading: false, error: null, data }); })
      .catch((error) => { if (!cancelled) setState({ loading: false, error, data: null }); });
    return () => { cancelled = true; };
  }, [memoFn, tick]);
  return { ...state, reload, refresh };
}

/**
 * Call `refresh` when the person comes back to this tab or window, so a
 * page left open picks up changes made elsewhere (e.g. the owner removed
 * them). At most once every 5 seconds.
 */
export function useRefreshOnFocus(refresh, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    let last = Date.now();
    const run = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 5000) return;
      last = Date.now();
      refresh();
    };
    window.addEventListener('focus', run);
    document.addEventListener('visibilitychange', run);
    return () => {
      window.removeEventListener('focus', run);
      document.removeEventListener('visibilitychange', run);
    };
  }, [refresh, enabled]);
}

export function PageHead({ title, sub, breadcrumb, actions, icon }) {
  return (
    <div className="page-head">
      <div>
        {breadcrumb && <div className="breadcrumb">{breadcrumb}</div>}
        <h1>{icon && <span aria-hidden="true" style={{ marginRight: 10 }}>{icon}</span>}{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function Section({ title, action, children, style }) {
  return (
    <div className="section" style={style}>
      {(title || action) && (
        <div className="section-head">
          <div className="section-title">{title}</div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function Spinner({ label = 'Loading…' }) {
  return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>{label}</div>;
}

export function ErrorNote({ error, onRetry }) {
  return (
    <div style={{ background: 'var(--red-bg)', color: 'var(--red-text)', padding: '12px 14px', borderRadius: 8, fontSize: '0.8125rem' }}>
      {error?.message || 'Something went wrong.'}
      {onRetry && <> · <a onClick={onRetry} style={{ cursor: 'pointer' }}>Retry</a></>}
    </div>
  );
}

export function Avatar({ name, src, size = 40, square = false, colorSeed, bg, radius }) {
  const [errored, setErrored] = useState(false);
  const dim = { width: size, height: size, borderRadius: radius ?? (square ? 8 : '50%'), flexShrink: 0 };
  // Reset the error flag if the src changes (e.g. after an icon upload).
  useEffect(() => { setErrored(false); }, [src]);
  // Stable color: if the caller passed a seed (like the entity id), hash
  // that so the tint doesn't shift while the user types a new name.
  const tintKey = colorSeed || name;

  if (src && !errored) {
    return (
      <img
        alt={name}
        src={src}
        onError={() => setErrored(true)}
        style={{ ...dim, objectFit: 'cover' }}
      />
    );
  }
  return (
    <div style={{
      ...dim, background: bg || hashTint(tintKey), color: bg && isLightColor(bg) ? '#111827' : '#fff', fontWeight: 700,
      fontSize: size * 0.42, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {initial(name)}
    </div>
  );
}

export function Empty({ children }) {
  return <div style={{ color: 'var(--muted)', fontSize: '0.84375rem', padding: '8px 0' }}>{children}</div>;
}
